import { Switch, Route } from "wouter";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ThemeProvider } from "@/components/ui/theme-provider";
import { Suspense, lazy, memo } from "react";
import { ProtectedRoute, AdminRoute } from "./lib/protected-route";
import { AuthProvider } from "@/hooks/use-auth";


// Import auth cleanup utility to prevent authentication errors
// import "@/utils/auth-cleanup"; // Commented out to fix build error

// Lazy load components for better performance
const NotFound = lazy(() => import("@/pages/not-found"));
const HomePage = lazy(() => import("@/pages/home-page"));
const AuthPage = lazy(() => import("@/pages/auth-page"));
const ManualsPage = lazy(() => import("@/pages/manuals-page"));
const RegulationsPage = lazy(() => import("@/pages/regulations-page"));
const AuditsPage = lazy(() => import("@/pages/audits-page"));
const ProfilePage = lazy(() => import("@/pages/profile-page"));
const AirlinesPage = lazy(() => import("@/pages/airlines-page"));
const OrganizationsPage = lazy(() => import("@/pages/organizations-page"));
const BillingPage = lazy(() => import("@/pages/billing-page"));
const RegulationCategoriesPage = lazy(() => import("@/pages/regulation-category-page"));
const SectionSelectPage = lazy(() => import("@/pages/create-audit/section-select-page"));
const SectionMappingPage = lazy(() => import("@/pages/create-audit/section-mapping-page"));
const AdminUsersPage = lazy(() => import("@/pages/admin-users-page"));
const AdminTechnicalProfilesPage = lazy(() => import("@/pages/admin-technical-profiles"));
const AuthoritiesPage = lazy(() => import("@/pages/authorities-page"));
const AuthCallback = lazy(() => import("@/pages/auth/callback"));
const SetupPasswordPage = lazy(() => import("@/pages/auth/setup-password"));
const RealtimeTestPage = lazy(() => import("@/pages/realtime-test"));

// Loading component
const LoadingSpinner = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
  </div>
);

const Router = memo(function Router() {
  return (
    <Suspense fallback={<LoadingSpinner />}>
      <Switch>
        <Route path="/" component={AuthPage} />
        <Route path="/auth" component={AuthPage} />
        
        {/* Auth flow routes */}
        <Route path="/auth/callback" component={AuthCallback} />
        <Route path="/auth/setup-password" component={SetupPasswordPage} />
        
        {/* Common routes for both user types - all protected */}
        <ProtectedRoute path="/home" component={HomePage} />
        <ProtectedRoute path="/profile" component={ProfilePage} />
        
        {/* Manuals (former Documents) routes */}
        <ProtectedRoute path="/manuals" component={ManualsPage} allowedRoles={["admin", "auditor"]} />
        
        {/* Audit routes */}
        <ProtectedRoute path="/audits" component={AuditsPage} />
        <ProtectedRoute path="/create-audit/:auditId/1" component={SectionSelectPage} />
        <ProtectedRoute path="/create-audit/:auditId/2" component={SectionMappingPage} />
        
        {/* Admin-only routes */}
        <AdminRoute path="/regulations" component={RegulationsPage} />
        <AdminRoute path="/regulation-category" component={RegulationCategoriesPage} />
        <AdminRoute path="/authorities" component={AuthoritiesPage} />
        <AdminRoute path="/organizations" component={OrganizationsPage} />
        <AdminRoute path="/admin-technical-profiles" component={AdminTechnicalProfilesPage} />
        <AdminRoute path="/admin-users" component={AdminUsersPage} />
        <AdminRoute path="/realtime-test" component={RealtimeTestPage} />
        
        {/* Billing - accessible to both admin and auditor */}
        <ProtectedRoute path="/billing" component={BillingPage} />
        
        {/* Legacy route redirects */}
        <ProtectedRoute path="/documents" component={ManualsPage} allowedRoles={["admin", "auditor"]} />
        <AdminRoute path="/regulation-categories" component={RegulationCategoriesPage} />
        <AdminRoute path="/airlines" component={OrganizationsPage} />
        
        <Route component={NotFound} />
      </Switch>
    </Suspense>
  );
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider defaultTheme="light" storageKey="aerosync-theme">
        <AuthProvider>
          <TooltipProvider>
            <Toaster />
            <Router />
          </TooltipProvider>
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
